/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { useThemeMode } from '../../../../_metronic/partials';
import {
	getCSS,
	getCSSVariableValue,
} from '../../../../_metronic/assets/ts/_utils';
import { toAbsoluteUrl } from '../../../../_metronic/helpers';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import request from '../../../../utils/request';
import { enAU } from 'date-fns/locale';
import { currencyFormat } from '../../../../utils';
moment.updateLocale(moment.locale('en', enAU), { invalidDate: '-' });

type Props = {
	className: string;
	statusColor?: string;
	lastUnit?: string;
};

const ChartMontly: React.FC<Props> = ({ className, statusColor = '' , lastUnit = ''}) => {
	let custNum = localStorage.getItem('customerNum');
	const chartRef = useRef<HTMLDivElement | null>(null);
	const { mode } = useThemeMode();

	const [dataChart, setDataChart] = useState({
		data: [],
		categories: [],
		unit: '',
		latestUsage: 0,
	});

	useEffect(() => {
		getMonthlyChartDashboard();
		setLoading(true);
	}, []);

	const _getSelectedDate = () => {
		const cd = searchParams.get('date') || undefined;
		return {
			fromDate: moment(cd).startOf('year').format('YYYY-MM-DD'),
			toDate:
				moment(cd).month() === 0
					? moment(cd).endOf('month').format('YYYY-MM-DD')
					: moment(cd)
							.add(-1, 'month')
							.endOf('month')
							.format('YYYY-MM-DD'),
		};
	};

	const getMonthlyChartDashboard = async () => {
		const payload: any = {
			CustomerNumber: custNum,
			FromDate: _getSelectedDate().fromDate,
			ToDate: _getSelectedDate().toDate,
			// CustomerNumber: '034774895',
			// FromDate: '2023-01-01',
			// ToDate: '2023-12-31',
		};
		await request
			.post(`dashboard/GetMonthlyChartDashboard`, payload)
			.then((res) => {
				setLoading(false);
				if (res?.status === 200 || res?.status === 201) {
					const data = res.data;
					if (data && data.length > 0) {
						const item = data[0];
						let isVol = false;
						if (
							(item.uom && item.uom.includes('M3')) ||
							item.uom === 'm³' ||
							item.uom.includes('<sup>')
						) {
							isVol = true;
						}
						const unit = isVol ? 'm³' : 'MMBtu';
						const dataMap = data.map((item: any) => {
							return isVol ? item.volume : item.energy;
						});
						const catMap = data.map((item: any, index: number) => {
							return item.measure_month
								? parseInt(item.measure_month)
								: index;
						});
						const total = data.reduce((value, item) => {
							if (isVol) {
								value += parseFloat(item.volume || 0);
							} else {
								value += parseFloat(item.energy || 0);
							}
							return value;
						}, 0);
						const chartData = {
							data: dataMap,
							categories: catMap,
							unit: unit,
							latestUsage: total, // / (data?.length || 1),
						};
						setDataChart(chartData);
					}
				} else {
					console.log('DATA IS EMPTY');
				}
			});
	};

	const [searchParams] = useSearchParams();

	const refreshMode = () => {
		if (!chartRef.current) {
			return;
		}

		const height = parseInt(getCSS(chartRef.current, 'height'));

		const chart = new ApexCharts(
			chartRef.current,
			getChartOptions(height, statusColor, dataChart, loading)
		);
		if (chart) {
			chart.render();
		}

		return chart;
	};

	useEffect(() => {
		const chart = refreshMode();

		return () => {
			if (chart) {
				chart.destroy();
			}
		};
	}, [/*chartRef, mode, */ statusColor, dataChart]);

	const [isLargeScreen, setIsLargeScreen] = useState(
		window.matchMedia('(min-width: 992px)').matches
	);
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		const handleResize = () => {
			setIsLargeScreen(window.matchMedia('(min-width: 992px)').matches);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	return (
		<div className={`card ${className}`}>
			<div style={{ height: '140px' }} className="card-header border-2">
				<h3 className="card-title align-items-start flex-column">
					<span className="card-label fw-bold fs-3 mb-3">
						Monthly chart
					</span>
					<span className="text-muted fw-bold fs-7">
						{moment(_getSelectedDate().fromDate).format('MMM YYYY')}{' '}
						- {moment(_getSelectedDate().toDate).format('MMM YYYY')}
					</span>
					<div>
						<span className="text-dark fw-bold fs-7">
							{currencyFormat(dataChart?.latestUsage)}{' '}
						</span>
						<span
							className="text-dark fw-bold fs-7"
							dangerouslySetInnerHTML={{
								__html: lastUnit || 'MMBtu',
							}}
						/>
					</div>
				</h3>
			</div>
			<div
				className="card-body"
				style={{
					overflowX: isLargeScreen ? 'auto' : 'scroll',
					overflowY: 'hidden',
				}}
			>
				{(!dataChart ||
					!dataChart?.data ||
					dataChart?.data?.length === 0) && (
					<div className="d-flex justify-content-center mt-0 flex-wrap mb-8">
						<img
							className="w-50"
							style={{ objectFit: 'scale-down' }}
							src={toAbsoluteUrl('/media/pgn/Empty state.png')}
							alt=""
						/>
						{/* <div className="w-100 text-center fs-5 fw-semibold">
							No data found.
						</div> */}
					</div>
				)}
				{loading ? (
					<span
						className="indicator-progress w-100 text-center"
						style={{ display: 'block' }}
					>
						Please wait...{' '}
						<span className="spinner-border spinner-border-sm align-middle"></span>
					</span>
				) : (
					<div
						hidden={
							!dataChart ||
							!dataChart?.data ||
							dataChart?.data?.length === 0
								? true
								: false
						}
						className={`${isLargeScreen ? '' : 'd-xs min-w-500px'}`}
						ref={chartRef}
						id="kt_charts_widget_3_chart"
						style={{ height: '350px' }}
					></div>
				)}
			</div>
		</div>
	);
};

export { ChartMontly };

function convertToMonthName(x) {
	if (!x || x?.length === 0) return ['-'];
	const m = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Okt',
	];
	return x.map((item) => {
		return m[parseInt(item) - 1];
	});
}

function getChartOptions(
	height: number,
	statusColor: string,
	dataChart?: any,
	loading?: boolean
): ApexOptions {
	const labelColor = getCSSVariableValue('--bs-gray-500');
	const borderColor = getCSSVariableValue('--bs-gray-200');
	const baseColor = '#00548b';
	const lightColor = getCSSVariableValue('--bs-info-light');
	let chartColor = baseColor;

	// if (statusColor == 'badge-light-warning') {
	// 	chartColor = '#00548b';
	// } else if (statusColor == 'badge-light-success') {
	// 	chartColor = '#b0ca29';
	// } else if (statusColor == 'badge-light-danger') {
	// 	chartColor = '#FF0000';
	// } else {
	// 	chartColor = '#6d6d80';
	// }

	return {
		// noData: {
		// 	text: loading ? 'Loading...' : 'No Data',
		// 	align: 'center',
		// 	verticalAlign: 'middle',
		// 	offsetX: 0,
		// 	offsetY: 0,
		// 	style: {
		// 		color: '#000000',
		// 		fontSize: '14px',
		// 		fontFamily: 'Helvetica',
		// 	},
		// },
		series: [
			{
				name: 'Usage',
				data: dataChart?.data || [],
				// data: [30, 40, 40, 90, 90, 70, 70, 90, 20, 100,],
			},
		],
		chart: {
			fontFamily: 'inherit',
			type: 'bar',
			height: 350,
			toolbar: {
				show: false,
			},
		},
		plotOptions: {},
		legend: {
			show: true,
		},
		dataLabels: {
			enabled: false,
		},
		fill: {
			type: 'solid',
			opacity: 1,
		},
		stroke: {
			curve: 'smooth',
			show: true,
			width: 3,
			colors: [chartColor, lightColor],
		},
		xaxis: {
			categories: convertToMonthName(dataChart?.categories || []),
			title: {
				text: 'Month',
				style: {
					fontWeight: '600',
				},
			},
			axisBorder: {
				show: true,
			},
			axisTicks: {
				show: false,
			},
			labels: {
				style: {
					colors: labelColor,
					fontSize: '12px',
				},
				// formatter: (value) => {
				// 	return currencyFormat(value);
				// },
			},
			// crosshairs: {
			//      position: 'front',
			//      stroke: {
			//           color: baseColor,
			//           width: 1,
			//           dashArray: 3,
			//      },
			// },
			// tooltip: {
			//      enabled: true,
			//      formatter: undefined,
			//      offsetY: 0,
			//      style: {
			//           fontSize: '12px',
			//      },
			// },
		},
		yaxis: {
			title: {
				text:
					dataChart?.unit &&
					(dataChart?.unit?.includes('M3') ||
						dataChart?.unit === 'm³' ||
						dataChart?.unit?.includes('<sup>'))
						? 'm³'
						: 'MMBtu',
				style: { fontWeight: '600' },
			},
			axisBorder: {
				show: true,
			},
			labels: {
				style: {
					colors: labelColor,
					fontSize: '12px',
				},
				formatter: function (value: any) {
					return currencyFormat(value);
				},
			},
		},
		states: {
			normal: {
				filter: {
					type: 'none',
					value: 0,
				},
			},
			hover: {
				filter: {
					type: 'none',
					value: 0,
				},
			},
			active: {
				allowMultipleDataPointsSelection: false,
				filter: {
					type: 'none',
					value: 0,
				},
			},
		},
		tooltip: {
			style: {
				fontSize: '12px',
			},
			x: {
				formatter: function (val) {
					return val.toString() + ' ' + moment().year();
				},
			},
			y: {
				formatter: function (val) {
					return (
						currencyFormat(val) + ' ' + dataChart?.unit || 'MMBtu'
					);
				},
			},
		},
		colors: [chartColor, lightColor],
		grid: {
			borderColor: borderColor,
			strokeDashArray: 4,
			yaxis: {
				lines: {
					show: false,
				},
			},
			xaxis: {
				lines: {
					show: false,
				},
			},
		},
		// markers: {
		//      strokeColors: baseColor,
		//      strokeWidth: 3,
		// },
	};
}
